import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Link, useNavigate } from "react-router-dom";
import { ModernLogo } from "@/components/ModernLogo";
import { 
  Users, 
  Shield, 
  AlertTriangle, 
  MessageSquare, 
  LogOut, 
  Home,
  BarChart3,
  Settings,
  UserCheck,
  Briefcase,
  MessageCircle
} from "lucide-react";
import AdminContactManagement from "@/components/AdminContactManagement";
import { AdminReportsManagement } from "@/components/AdminReportsManagement";
import { AdminSecurityDashboard } from "@/components/AdminSecurityDashboard";
import AdminTradesmanManagement from "@/components/AdminTradesmanManagement";
import AdminCustomerManagement from "@/components/AdminCustomerManagement";
import { AdminJobsManagement } from "@/components/AdminJobsManagement";
import { AdminChatManagement } from "@/components/AdminChatManagement";
import { LiveSupportManagement } from "@/components/LiveSupportManagement";
import { AdminAuditLog } from "@/components/AdminAuditLog";
import { AdminLiveAccessMap } from "@/components/AdminLiveAccessMap";
import { AdminFeedbackManagement } from "@/components/AdminFeedbackManagement";

interface Profile {
  id: string;
  full_name: string;
  email: string;
  user_type: string;
  is_suspended: boolean;
  created_at: string;
}

interface Stats {
  totalUsers: number;
  totalCustomers: number;
  totalTradesmen: number;
  activeJobs: number;
  pendingReports: number;
  unreadSupportMessages: number;
  securityIssues: number;
}

export const AdminDashboard = () => {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    totalCustomers: 0,
    totalTradesmen: 0,
    activeJobs: 0,
    pendingReports: 0,
    unreadSupportMessages: 0,
    securityIssues: 0
  });
  const [recentUsers, setRecentUsers] = useState<Profile[]>([]);
  const [hasNewReports, setHasNewReports] = useState(false);
  const [lastReportCount, setLastReportCount] = useState(0);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAccess();
    fetchStats();
    fetchRecentUsers();
    const cleanup = setupRealtimeSubscriptions();
    return cleanup;
  }, []);


  const setupRealtimeSubscriptions = () => {
    // Subscribe to new reports
    const reportsChannel = supabase
      .channel(`admin_reports_${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_reports'
        },
        (payload) => {
          console.log('New user report:', payload);
          setHasNewReports(true);
          toast({
            title: "New Report",
            description: "A new user report has been submitted.",
            variant: "default",
          });
          fetchStats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_reports'
        },
        (payload) => {
          console.log('New chat report:', payload);
          setHasNewReports(true);
          toast({
            title: "New Report",
            description: "A new chat report has been submitted.",
            variant: "default",
          });
          fetchStats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'admin_message_reports'
        },
        (payload) => {
          console.log('New message report:', payload);
          setHasNewReports(true);
          toast({
            title: "New Report", 
            description: "A new message report has been submitted.",
            variant: "default",
          });
          fetchStats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_reports'
        },
        () => fetchStats()
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_reports'
        },
        () => fetchStats()
      )
      .subscribe();

    // Subscribe to new support messages
    const supportChannel = supabase
      .channel(`admin_support_${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'support_chats'
        },
        () => fetchStats()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'support_messages'
        },
        () => fetchStats()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(reportsChannel);
      supabase.removeChannel(supportChannel);
    };
  };

  const checkAdminAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      console.log("AdminDashboard: Current user:", user?.id, user?.email);
      
      if (!user) {
        console.log("AdminDashboard: No user found, redirecting to home");
        navigate('/');
        return;
      }

      setUser(user);

      // Check if user has admin role
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();

      console.log("AdminDashboard: User roles check:", { userRoles, rolesError });

      if (rolesError) {
        console.error("AdminDashboard: Error fetching user roles:", rolesError);
        toast({
          title: "Access Error",
          description: "Failed to verify admin privileges.",
          variant: "destructive",
        });
        navigate('/');
        return;
      }

      if (!userRoles) {
        console.log("AdminDashboard: User is not an admin");
        toast({
          title: "Access Denied",
          description: "You don't have admin privileges.",
          variant: "destructive",
        });
        navigate('/');
        return;
      }

      // Get profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      console.log("AdminDashboard: Profile data:", { profileData, profileError });

      setProfile(profileData);
      setIsLoading(false);
    } catch (error) {
      console.error('Error checking admin access:', error);
      navigate('/');
    }
  };

  const fetchStats = async () => {
    try {
      // Get total users
      const { count: totalUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Get customers
      const { count: totalCustomers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('user_type', 'customer');

      // Get tradesmen
      const { count: totalTradesmen } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('user_type', 'tradesman');

      // Get active jobs (excluding completed and cancelled)
      const { count: activeJobs } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .in('status', ['pending', 'assigned', 'in_progress', 'quote_accepted']);

      // Get pending reports (user + chat)
      const [userPendingRes, chatPendingRes] = await Promise.all([
        supabase
          .from('user_reports')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('chat_reports')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending')
      ]);

      const pendingReports = (userPendingRes.count || 0) + (chatPendingRes.count || 0);

      // Get unread support messages (new status messages from customers)
      const { count: unreadSupport } = await supabase
        .from('support_chats')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'new');

      // Get security issues (suspended users + blocked messages as simple metric)
      const [suspendedUsersRes, blockedMessagesRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('is_suspended', true),
        supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('is_blocked', true)
      ]);

      const securityIssues = (suspendedUsersRes.count || 0) + (blockedMessagesRes.count || 0);

      setStats({
        totalUsers: totalUsers || 0,
        totalCustomers: totalCustomers || 0,
        totalTradesmen: totalTradesmen || 0,
        activeJobs: activeJobs || 0,
        pendingReports,
        unreadSupportMessages: unreadSupport || 0,
        securityIssues
      });

      // Check if report count increased to trigger notification
      if (lastReportCount > 0 && pendingReports > lastReportCount) {
        setHasNewReports(true);
      }
      setLastReportCount(pendingReports);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRecentUsers = async () => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name, email, user_type, is_suspended, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      setRecentUsers(data || []);
    } catch (error) {
      console.error('Error fetching recent users:', error);
    }
  };

  const handleReportsTabClick = () => {
    setHasNewReports(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                <Link to="/" className="flex items-center gap-2 text-primary hover:text-primary/80 shrink-0">
                  <Home className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="text-xs sm:text-sm whitespace-nowrap">Back to Site</span>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  <ModernLogo size="sm" showTagline={false} />
                  <p className="text-xs text-muted-foreground hidden sm:block">Administration</p>
                </div>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                <div className="text-left sm:text-right">
                  <p className="text-xs sm:text-sm font-medium truncate max-w-[150px] sm:max-w-none">{profile?.full_name}</p>
                  <Badge variant="secondary" className="text-xs">Administrator</Badge>
                </div>
                <Button onClick={handleSignOut} variant="outline" size="sm" className="shrink-0">
                  <LogOut className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Sign Out</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUsers}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Customers</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalCustomers}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tradesmen</CardTitle>
              <Settings className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalTradesmen}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Jobs</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeJobs}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Reports</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.pendingReports}</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="overview" className="space-y-6">
          <div className="w-full overflow-x-auto pb-2">
            <TabsList className="inline-flex w-max min-w-full gap-1 h-auto p-1">
            <TabsTrigger value="overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
            <TabsTrigger value="jobs" className="text-xs sm:text-sm">Jobs</TabsTrigger>
            <TabsTrigger value="customers" className="text-xs sm:text-sm">Customers</TabsTrigger>
            <TabsTrigger value="tradesmen" className="text-xs sm:text-sm">Tradesmen</TabsTrigger>
            <TabsTrigger value="chats" className="text-xs sm:text-sm">
              <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
              <span className="hidden sm:inline">Chats</span>
            </TabsTrigger>
            <TabsTrigger 
              value="live-support" 
              className={`text-xs sm:text-sm ${stats.unreadSupportMessages > 0 ? 'animate-pulse bg-red-50 text-red-600 border-red-200' : ''}`}
            >
              <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
              <span className="hidden sm:inline">Live Support</span>
              {stats.unreadSupportMessages > 0 && (
                <Badge variant="destructive" className="ml-1 sm:ml-2 animate-pulse text-[10px] sm:text-xs h-4 sm:h-5 px-1">
                  {stats.unreadSupportMessages}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="live-access" className="text-xs sm:text-sm whitespace-nowrap">
              Live Access
            </TabsTrigger>
            <TabsTrigger 
              value="security" 
              className={`text-xs sm:text-sm ${stats.securityIssues > 0 ? 'animate-pulse bg-orange-50 text-orange-600 border-orange-200' : ''}`}
            >
              Security
              {stats.securityIssues > 0 && (
                <Badge variant="destructive" className="ml-1 sm:ml-2 animate-pulse text-[10px] sm:text-xs h-4 sm:h-5 px-1">
                  {stats.securityIssues}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="reports" 
              className={`text-xs sm:text-sm relative ${
                stats.pendingReports > 0 || hasNewReports 
                  ? 'animate-pulse bg-red-50 text-red-600 border-red-200' 
                  : ''
              }`}
              onClick={handleReportsTabClick}
            >
              Reports
              {hasNewReports && (
                <div className="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full animate-ping"></div>
              )}
              {stats.pendingReports > 0 && (
                <Badge variant="destructive" className="ml-1 sm:ml-2 animate-pulse text-[10px] sm:text-xs h-4 sm:h-5 px-1">
                  {stats.pendingReports}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="contacts" className="text-xs sm:text-sm">Contacts</TabsTrigger>
            <TabsTrigger value="feedback" className="text-xs sm:text-sm">Feedback</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent User Registrations</CardTitle>
                  <CardDescription>Latest users who joined the platform</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentUsers.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">{user.full_name}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                          <p className="text-xs text-muted-foreground">
                            Joined {new Date(user.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={user.user_type === 'tradesman' ? 'default' : 'secondary'}>
                            {user.user_type}
                          </Badge>
                          {user.is_suspended && (
                            <Badge variant="destructive">Suspended</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="jobs">
            <AdminJobsManagement />
          </TabsContent>

          <TabsContent value="customers">
            <AdminCustomerManagement />
          </TabsContent>

        <TabsContent value="tradesmen">
          <AdminTradesmanManagement />
        </TabsContent>

        <TabsContent value="chats">
          <AdminChatManagement />
        </TabsContent>

        <TabsContent value="live-support">
          <Card>
            <CardHeader>
              <CardTitle>Live Support Management</CardTitle>
              <CardDescription>
                Manage customer support chats in real-time
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-[600px]">
                <LiveSupportManagement />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="live-access">
          <Card>
            <CardHeader>
              <CardTitle>Live Website Access</CardTitle>
              <CardDescription>
                Monitor real-time website access and user locations
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="p-6">
                <AdminLiveAccessMap />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security">
            <AdminSecurityDashboard />
          </TabsContent>

          <TabsContent value="audit-log">
            <AdminAuditLog />
          </TabsContent>

          <TabsContent value="reports">
            <AdminReportsManagement />
          </TabsContent>

          <TabsContent value="contacts">
            <AdminContactManagement />
          </TabsContent>

          <TabsContent value="feedback">
            <AdminFeedbackManagement />
          </TabsContent>
        </Tabs>
      </div>

    </div>
  );
};